import { useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Skeleton,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColumnsType } from 'antd/es/table'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { formatMoneyValue, money } from '@/shared/lib/money'
import { EmptyState } from '@/shared/ui/EmptyState'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { PageHeader } from '@/shared/ui/PageHeader'
import { PAYROLL_STATE_COLOR } from '@/features/hr/leaveState'
import { actorLabel } from '@/features/identity/actorName'
import { useActorDirectory } from '@/features/identity/useActorDirectory'
import {
  PayrollDecisionModal,
  type PayrollAction,
  type PayrollDecisionResult,
} from '@/features/hr/PayrollDecisionModal'
import {
  canApprove,
  canCancel,
  canMarkPaid,
  canRegenerate,
  fetchPayrollLines,
  fetchPayrollRun,
  isKnownOrigin,
  isSelfApproval,
  periodLabel,
  regeneratePayrollRun,
  type PayrollLine,
  type PayrollLineItem,
} from '@/features/hr/payrollApi'

/**
 * Bitta vedomost — satrlari va boshqaruvi.
 *
 * <h2>⚠️ O'Z VEDOMOSTINI TASDIQLASH — NATIJA SARIQ BO'LADI</h2>
 * Server ruxsat bergan holatda ham (yagona tasdiqlovchi bo'lsangiz) bu
 * amal auditda `selfApproved: true` bilan yoziladi. Panelning javobi
 * shunga mos: yashil «bajarildi» EMAS, sariq OGOHLANTIRISH. Sababi
 * `OperationResultAlert` dagi `PARTIAL` bilan bir xil mulohaza — panel
 * hech qachon yolg'on tinchlik bermaydi.
 *
 * ⚠️ Server javobida bu bayroq YO'Q (`selfApproved` faqat auditga
 * yoziladi), shuning uchun uni panel o'zi hisoblaydi
 * (`isSelfApproval`) — `PayrollDecisionModal` javadoc'iga qarang.
 *
 * <h2>⚠️ SATRLAR MUZLAGAN</h2>
 * `orgNodeId`, `jobTitleId` va qism kodlari yig'ilgan PAYTDAGI nusxa.
 * Xodim keyin boshqa filialga ko'chsa ham vedomost o'zgarmaydi — aks
 * holda o'tgan oy hisoboti bugungi tuzilma bilan qayta yozilardi.
 */
export function PayrollRunDetailPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { id = '' } = useParams<{ id: string }>()
  const { user } = useAuth()
  const { can } = usePermissions()

  // ⚠️ N4: uchala ustun ham (`generated_by`, `approved_by`, `paid_by`)
  //    `auth.getName()` dan keladi — aktyor identifikatori, NOM emas.
  const actors = useActorDirectory()

  const [action, setAction] = useState<PayrollAction | null>(null)
  const [decision, setDecision] = useState<PayrollDecisionResult | null>(null)

  const run = useQuery({
    queryKey: queryKeys.hr.payrollRun(id),
    queryFn: () => fetchPayrollRun(id),
    enabled: id !== '',
  })

  const lines = useQuery({
    queryKey: queryKeys.hr.payrollLines(id),
    queryFn: () => fetchPayrollLines(id),
    enabled: id !== '',
  })

  /**
   * ⚠️ Qayta yig'ish OYNASIZ: u yangi ma'lumot so'ramaydi va faqat
   * LOYIHA holatida ochiq. Xatosi esa sahifada ko'rinadi — bu tugma
   * hech qanday oynaga bog'lanmagan, ya'ni niqob ortida qolish xavfi
   * yo'q (`guards.mjs` 1-bo'limi aynan modalga bog'langan
   * mutatsiyalar haqida).
   */
  const regenerate = useMutation({
    mutationFn: () => regeneratePayrollRun(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.all })
    },
  })

  const back = (
    <Button icon={<ArrowLeftOutlined />} onClick={() => void navigate('/hr/payroll')}>
      {t('common.back')}
    </Button>
  )

  if (run.isPending) {
    return (
      <>
        <PageHeader title={t('nav.hrPayroll')} extra={back} />
        <Card>
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      </>
    )
  }

  if (run.isError || !run.data) {
    return (
      <>
        <PageHeader title={t('nav.hrPayroll')} extra={back} />
        <Card>
          {/* ⚠️ Doiradan tashqaridagi vedomost ham AYNAN shu yerga
              keladi va 404 bo'ladi: 403 «bunday vedomost BOR» degan
              ma'lumotni oshkor qilardi */}
          <EmptyState
            variant="error"
            title={t('hr.payroll.notFound')}
            description={translateError(run.error, t)}
          />
        </Card>
      </>
    )
  }

  const row = run.data
  const self = isSelfApproval(row, user?.username ?? null)
  const canDecide = can('hr.payroll.approve')

  const itemColumns = (currency: string): ColumnsType<PayrollLineItem> => [
    {
      title: t('hr.salary.component'),
      key: 'component',
      render: (_value, item) =>
        item.componentCode ? t(`seed.payComponents.${item.componentCode}`, { defaultValue: item.componentCode }) : '—',
    },
    {
      title: t('hr.salary.kind'),
      key: 'kind',
      render: (_value, item) => (
        <Tag color={item.kind === 'EARNING' ? 'green' : 'volcano'}>
          {t(`payComponentKind.${item.kind}`)}
        </Tag>
      ),
    },
    {
      title: t('hr.payroll.origin'),
      key: 'origin',
      render: (_value, item) =>
        isKnownOrigin(item.origin) ? t(`payOrigin.${item.origin}`) : t('common.dash'),
    },
    {
      title: t('hr.salary.amount'),
      key: 'amount',
      align: 'right',
      render: (_value, item) => formatMoneyValue(money(item.amountMinor, currency)),
    },
    {
      title: t('hr.payEvent.noteField'),
      key: 'note',
      render: (_value, item) => item.note ?? '—',
    },
  ]

  const lineColumns: ColumnsType<PayrollLine> = [
    {
      title: t('hr.leave.person'),
      key: 'person',
      render: (_value, line) => (
        <Typography.Link onClick={() => void navigate(`/hr/persons/${line.personId}`)}>
          {line.fullName ?? t('hr.person.unknownName')}
        </Typography.Link>
      ),
    },
    {
      title: t('hr.person.personnelNo'),
      key: 'personnelNo',
      render: (_value, line) => line.personnelNo ?? '—',
    },
    {
      title: t('hr.payroll.orgNode'),
      key: 'orgNode',
      render: (_value, line) => line.orgNodeName ?? '—',
    },
    {
      title: t('hr.payroll.jobTitle'),
      key: 'jobTitle',
      render: (_value, line) => line.jobTitleName ?? '—',
    },
    {
      title: t('hr.payroll.earning'),
      key: 'earning',
      align: 'right',
      render: (_value, line) => formatMoneyValue(line.earning),
    },
    {
      title: t('hr.payroll.deduction'),
      key: 'deduction',
      align: 'right',
      render: (_value, line) => formatMoneyValue(line.deduction),
    },
    {
      title: t('hr.payroll.net'),
      key: 'net',
      align: 'right',
      render: (_value, line) => (
        <Typography.Text strong>{formatMoneyValue(line.net)}</Typography.Text>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={`${t('nav.hrPayroll')} · ${periodLabel(row)}`}
        description={row.orgNodeName ?? row.orgNodeCode ?? ''}
        extra={
          <Space wrap>
            {back}
            {can('hr.payroll.run') && canRegenerate(row) ? (
              <Button loading={regenerate.isPending} onClick={() => regenerate.mutate()}>
                {t('hr.payroll.regenerateAction')}
              </Button>
            ) : null}
            {canDecide && canApprove(row) ? (
              <Button type="primary" onClick={() => setAction('APPROVE')}>
                {t('hr.payroll.action.APPROVE.submit')}
              </Button>
            ) : null}
            {canDecide && canMarkPaid(row) ? (
              <Button type="primary" onClick={() => setAction('PAID')}>
                {t('hr.payroll.action.PAID.submit')}
              </Button>
            ) : null}
            {canDecide && canCancel(row) ? (
              <Button danger onClick={() => setAction('CANCEL')}>
                {t('hr.payroll.action.CANCEL.submit')}
              </Button>
            ) : null}
          </Space>
        }
      />

      {/*
        ⚠️ NATIJA IKKI XIL: oddiy tasdiqlash — YASHIL, o'zini
        tasdiqlash — SARIQ. Ikkinchisi xato emas, lekin u audit izida
        alohida belgilanadi va odam buni BILISHI kerak.
      */}
      {decision === null ? null : decision.selfApproved ? (
        <Alert
          type="warning"
          showIcon
          closable
          onClose={() => setDecision(null)}
          style={{ marginBottom: 16 }}
          message={t('hr.payroll.selfApprovedDoneTitle')}
          description={
            <ExpandableHint text={t('hr.payroll.selfApprovedDoneText')} fontSize={13} />
          }
        />
      ) : (
        <div style={{ marginBottom: 16 }}>
          <OperationResultAlert partialNote={false}
            result={decision.result}
            closable
            onClose={() => setDecision(null)}
          />
        </div>
      )}

      {regenerate.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(regenerate.error, t)}
        />
      ) : null}

      {row.state === 'DRAFT' && self ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('hr.payroll.selfApproveTitle')}
          description={<ExpandableHint text={t('hr.payroll.selfApproveText')} fontSize={13} />}
        />
      ) : null}

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card title={t('hr.payroll.summary')}>
          <Descriptions column={{ xs: 1, md: 2 }} size="small" bordered>
            <Descriptions.Item label={t('hr.payroll.state')}>
              <Tag color={PAYROLL_STATE_COLOR[row.state]}>{t(`payrollState.${row.state}`)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('hr.payroll.lineCount')}>{row.lineCount}</Descriptions.Item>
            <Descriptions.Item label={t('hr.payroll.earning')}>
              {formatMoneyValue(row.earning)}
            </Descriptions.Item>
            <Descriptions.Item label={t('hr.payroll.deduction')}>
              {formatMoneyValue(row.deduction)}
            </Descriptions.Item>
            <Descriptions.Item label={t('hr.payroll.net')} span={2}>
              <Typography.Text strong>{formatMoneyValue(row.net)}</Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('hr.payroll.generatedBy')}>
              {actorLabel(row.generatedBy, actors, t('common.dash'))}
              {row.generatedAt === null ? null : (
                <Tooltip title={utcTooltip(row.generatedAt)}>
                  <Typography.Text type="secondary">
                    {` · ${formatDateTime(row.generatedAt)}`}
                  </Typography.Text>
                </Tooltip>
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('hr.payroll.approvedBy')}>
              {actorLabel(row.approvedBy, actors, t('common.dash'))}
              {row.approvedAt === null ? null : (
                <Tooltip title={utcTooltip(row.approvedAt)}>
                  <Typography.Text type="secondary">
                    {` · ${formatDateTime(row.approvedAt)}`}
                  </Typography.Text>
                </Tooltip>
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('hr.payroll.paidBy')} span={2}>
              {actorLabel(row.paidBy, actors, t('common.dash'))}
              {row.paidAt === null ? null : (
                <Tooltip title={utcTooltip(row.paidAt)}>
                  <Typography.Text type="secondary">
                    {` · ${formatDateTime(row.paidAt)}`}
                  </Typography.Text>
                </Tooltip>
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('hr.payroll.note')} span={2}>
              {row.note === null ? '—' : <ExpandableHint text={row.note} />}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title={t('hr.payroll.linesTitle')}>
          {lines.isError ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={translateError(lines.error, t)}
            />
          ) : null}
          {/* ⚠️ Bo'sh xulosa FAQAT `isSuccess` da (`guards.mjs` 3-bo'limi) */}
          {lines.isSuccess && (lines.data ?? []).length === 0 ? (
            <EmptyState
              title={t('hr.payroll.emptyLines')}
              description={t('hr.payroll.emptyLinesHint')}
            />
          ) : (
            <Table<PayrollLine>
              rowKey={(line) => line.id}
              columns={lineColumns}
              dataSource={[...(lines.data ?? [])]}
              loading={lines.isPending}
              pagination={false}
              size="small"
              scroll={{ x: 960 }}
              expandable={{
                expandedRowRender: (line) => (
                  <Table<PayrollLineItem>
                    rowKey={(item) => item.id}
                    columns={itemColumns(line.net.currency)}
                    dataSource={[...line.items]}
                    pagination={false}
                    size="small"
                  />
                ),
              }}
            />
          )}
        </Card>
      </Space>

      {action === null ? null : (
        <PayrollDecisionModal
          action={action}
          run={row}
          username={user?.username ?? null}
          onClose={() => setAction(null)}
          onDone={setDecision}
        />
      )}
    </>
  )
}
